import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeysConfig } from 'src/app/configs/keys.config';
import { TokenData } from '../interface/token.interface';
import moment from 'moment';
import { Observable, Subscription, catchError, delay, finalize, interval, of, throwError, timer } from 'rxjs';

type TokenMixExp = TokenData & { expireDate?: Date; }

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _storage = localStorage;
  private _tkSub?: Subscription;
  private _token?: TokenMixExp = JSON.parse(this._storage.getItem(KeysConfig.tokenAuth) || 'null') || undefined;

  constructor(private _http: HttpClient) {
    this._storage.removeItem(KeysConfig.accessToken);
    this._storage.removeItem(KeysConfig.refreshToken);
  }

  /** ยืนยันตัวตน */
  async authorization() {
    if (this._token) {
      const { expireDate } = this._token;
      const seconds = moment.duration(moment(expireDate).diff(moment())).asSeconds() || 0;
      this._automatedRefreshToken(seconds);
      return true;
    }
    return false;
  }

  /** ขอ token ใหม่ด้วย refresh token */
  updateToken() {
    const data = { "refresh_token": this.refreshToken };
    return this._http.post<TokenData>('/api/Authentication/refreshtoken', data);
  }

  /** เข้าสู่ระบบด้วย Google (สำหรับผู้ตอบแบบสอบถามที่ require_login) — ส่ง Google ID token ให้ backend ตรวจสอบ/ออก token ของระบบเอง
   *  response มี shape เดียวกับ signin ปกติ (data.token_type === 'Bearer') → HttpConfig interceptor จะเรียก saveToken ให้เอง
   *  เหมือน AccountService.postSignin จึงไม่ต้อง saveToken เองในนี้ */
  signInWithGoogle(googleIdToken: string) {
    return this._http.post('/api/Authentication/google-signin', { id_token: googleIdToken });
  }

  /** บันทึกข้อมูล token */
  saveToken(token: TokenData | undefined) {
    this._token = token;
    if (this._token) {
      this._token.expireDate = moment().add(this._token.expires_in, 'seconds').toDate();
      // เก็บลง localStorage เฉพาะ metadata วันหมดอายุ — token จริงอยู่ใน HttpOnly cookie อยู่แล้ว
      // จงใจไม่ persist field access_token/refresh_token (backend ส่งค่าว่างมา) เพื่อไม่ให้ scanner
      // มองว่ามี token หลุดใน localStorage (ZAP: "Information Disclosure - Information in Browser localStorage")
      const meta: TokenMixExp = { expires_in: this._token.expires_in, expireDate: this._token.expireDate } as TokenMixExp;
      this._storage.setItem(KeysConfig.tokenAuth, JSON.stringify(meta));
      this._automatedRefreshToken(this._token.expires_in);
    }
    else this._storage.removeItem(KeysConfig.tokenAuth);
  }

  /** สถานะล็อกอิน (token จริงอยู่ใน HttpOnly cookie — JS อ่านไม่ได้) */
  get isLoggedIn() {
    return !!this._token;
  }

  /** ข้อมูล access token (ไม่ใช้แล้ว — token อยู่ใน cookie) */
  get accessToken() {
    if (this._token?.access_token) return this._token.access_token;
    return undefined;
  }

  /** ข้อมูล refresh token (ไม่ใช้แล้ว — token อยู่ใน cookie) */
  get refreshToken() {
    if (this._token?.refresh_token) return this._token.refresh_token;
    return undefined;
  }

  /** ต่ออายุ token อัตโนมัติ ต่ออายุก่อน 60 วินาที */
  private _automatedSec: number = 60;
  private _automatedRefreshToken(seconds: number) {
    seconds = Math.round(seconds - this._automatedSec);
    this._tkSub?.unsubscribe();
    this._tkSub = timer(seconds * 1000).subscribe(() => {
      this._tkSub?.unsubscribe();
      this.updateToken().subscribe({
        error: () => {
          this.saveToken(undefined);
          location.reload();
        }
      });
    });
  }
}
